import Collection from 'ol/Collection';
import Draw from 'ol/interaction/Draw';
import { GeoJSON } from 'ol/format';
import Select from 'ol/interaction/Select';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import { Style, Stroke, Fill, Circle } from 'ol/style';
import vuetify from '@/plugins/vuetify.js';

const vuetifyColors = vuetify.preset.theme.themes.light;

export default {
  data: () => ({
    editLayer: {},
    isAddNewFeatureActive: false,
    isFeatureActive: false,
    isUserLayerEditing: false,
    selectedUserFeature: undefined,
  }),
  computed: {
    editingLayer() {
      return this.$store.getters.getEditingLayer;
    },
    editingTypeIcon() {
      const layer = this.userLayers.find(
        uL => uL.name_pl === this.editingLayer
      );
      if (layer.geometry_type === 'line') {
        return 'mdi-chart-timeline-variant';
      } else if (layer.geometry_type === 'point') {
        return 'mdi-plus-circle-outline';
      } else {
        return 'mdi-shape-polygon-plus';
      }
    },
    userLayers() {
        return this.$store.getters.getUserLayersList;
    },
  },
  methods: {
    addFeature() {
      if (this.getInteractionByName('selectUserFeature')) {
        this.map.removeInteraction(
          this.getInteractionByName('selectUserFeature')
        );
      }
      this.createUserDrawInteraction();
      this.getInteractionByName('userDraw').setActive(true);
    },
    addFeatureToUserLayer(layerName, layerFeatures) {
      const features = [];
      for (let layerFeature of layerFeatures) {
        const feature = new GeoJSON().readFeature(layerFeature.geometry, {
          featureProjection: 'EPSG:3857',
          dataProjection: 'EPSG:3857'
        });
        for (const key in layerFeature) {
          if (key !== 'geometry') {
            feature.set(key, layerFeature[key]);
          }
        }
        features.push(feature);
      }
      this.getLayerByName(layerName)
        .getSource()
        .addFeatures(features);
    },
    addUserLayer(layer) {
      this.map.addLayer(
        new VectorLayer({
          name: layer.name_pl,
          source: new VectorSource({
            features: new Collection([]),
            wrapX: false
          }),
          style: feature => {
            if (layer.style.type === 'unique') {
              const value = feature.get('properties')[layer.style.attribute];
              const style = layer.style.uniques[value];
              return new Style({
                fill: new Fill({
                  color: style ? style.fillColor : vuetifyColors.accent,
                }),
                stroke: new Stroke({
                  color: style ? style.strokeColor : vuetifyColors.accent,
                  width: style ? style.strokeWidth : 2
                }),
                image: new Circle({
                  radius: style ? style.strokeWidth : 2,
                  stroke: new Stroke({
                    color: style ? style.strokeColor : vuetifyColors.accent,
                  }),
                  fill: new Fill({
                    color: style ? style.fillColor : vuetifyColors.accent
                  })
                })
              })
            } else {
              return new Style({
                fill: new Fill({
                  color: layer.style ? layer.style.fillColor : vuetifyColors.accent
                }),
                stroke: new Stroke({
                  color: layer.style ? layer.style.strokeColor : vuetifyColors.accent,
                  width: layer.style ? layer.style.strokeWidth : 2
                }),
                image: new Circle({
                  radius: 5,
                  stroke: new Stroke({
                    color: layer.style ? layer.style.strokeColor : vuetifyColors.accent
                  }),
                  fill: new Fill({
                    color: layer.style ? layer.style.fillColor : vuetifyColors.accent
                  })
                })
              })
            }
          },
        })
      );
      this.$store.dispatch('getUserLayerGeometry', layer.id).then(r => {
        if (r.status === 200) {
          const layerFeatures = r.data.features;
          const features = [];
          for (let layerFeature of layerFeatures) {
            const feature = new GeoJSON().readFeature(layerFeature.geometry, {
              featureProjection: 'EPSG:3857',
              dataProjection: 'EPSG:3857'
            });
            for (const key in layerFeature) {
              if (key !== 'geometry') {
                feature.set(key, layerFeature[key]);
              }
            }
            features.push(feature);
          }
          this.getLayerByName(layer.name_pl)
            .getSource()
            .addFeatures(features);
        } else {
          this.$root.$emit(
            'showSnackbar',
            this.$i18n.t(`dashboard.map.getFeatureError`)
          );
        }
      });
    },
    cancelUserFeature() {
      this.getLayerByName('userLayerDraw')
        .getSource()
        .clear();
      this.isAddNewFeatureActive = false;
    },
    clearUserFeatureSelection() {
      if (this.getInteractionByName('selectUserFeature')) {
        this.getInteractionByName('selectUserFeature')
          .getFeatures()
          .clear();
      }
    },
    createUserDrawInteraction() {
      if (this.getInteractionByName('userDraw')) {
        map.removeInteraction(this.getInteractionByName('userDraw'));
      }
      let type = '';
      if (this.editLayer.geometry_type === 'polygon') {
        type = 'Polygon';
      } else if (this.editLayer.geometry_type === 'line') {
        type = 'LineString';
      } else {
        type = 'Point';
      }
      if (!this.getLayerByName('userLayerDraw')) {
        this.initDraw();
      }
      const userDraw = new Draw({
        source: this.getLayerByName('userLayerDraw').getSource(),
        type: type,
        style: new Style({
          fill: new Fill({
            color: 'rgba(63, 127, 191, 0.7)'
          }),
          stroke: new Stroke({
            color: 'rgba(0, 0, 255, 1)',
            width: 2
          }),
          image: new Circle({
            radius: 5,
            stroke: new Stroke({
              color: 'rgba(0, 0, 255, 1)'
            }),
            fill: new Fill({
              color: 'rgba(63, 127, 191, 0.7)'
            })
          })
        })
      });
      userDraw.set('name', 'userDraw');
      userDraw.setActive(false);
      this.map.addInteraction(userDraw);
      userDraw.on('drawend', () => {
        userDraw.setActive(false);
        this.editLayer = this.$store.getters.getUserLayersList.find(
          uL => uL.name_pl === this.editingLayer
        );
        this.isAddNewFeatureActive = true;
      });
    },
    deleteFeature() {
      const layer = this.editLayer;
      const featureId = this.selectedUserFeature.get('id');
      this.$store
        .dispatch('deleteFeature', {
          layerId: layer.id,
          featureId: featureId
        })
        .then(r => {
          if (r.status === 200) {
            this.clearUserFeatureSelection();
            const feature = this.getLayerByName(layer.name_pl)
              .getSource()
              .getFeatures()
              .find(f => f.get('id') === featureId);
            this.getLayerByName(layer.name_pl)
              .getSource()
              .removeFeature(feature);
            this.isFeatureActive = false;
            this.$root.$emit(
              'showSnackbar',
              this.$i18n.t('dashboard.map.featureDeleteSuccess')
            );
          } else {
            this.$root.$emit(
              'showSnackbar',
              this.$i18n.t('dashboard.map.featureDeleteError')
            );
          }
        })
        .catch(e => {
          this.$root.$emit('showSnackbar', e);
        });
    },
    hideFeature() {
      this.clearUserFeatureSelection();
      this.isFeatureActive = false;
    },
    initDraw() {
      let drawUserLayer = new VectorLayer({
        name: 'userLayerDraw',
        source: new VectorSource(),
        style: new Style({
          fill: new Fill({
            color: 'rgba(63, 127, 191, 0.7)'
          }),
          stroke: new Stroke({
            color: 'rgba(0, 0, 255, 1)',
            width: 2
          }),
          image: new Circle({
            radius: 5,
            stroke: new Stroke({
              color: 'rgba(0, 0, 255, 1)'
            }),
            fill: new Fill({
              color: 'rgba(63, 127, 191, 0.7)'
            })
          })
        })
      });
      this.map.addLayer(drawUserLayer);
    },
    layerEdit() {
      if (this.isUserLayerEditing) {
        if (this.getInteractionByName('selectUserFeature')) {
          this.map.removeInteraction(
            this.getInteractionByName('selectUserFeature')
          );
        }
        this.getInteractionByName('userDraw')
          ? this.getInteractionByName('userDraw').setActive(false)
          : false;
        this.editLayer = {};
        this.$refs.map_legend.userLayerEditing();
        this.isUserLayerEditing = false;
      } else {
        this.editLayer = this.userLayers.find(
          uL => uL.name_pl === this.editingLayer
        );
        this.$refs.map_legend.userLayerEditing();
        this.isUserLayerEditing = true;
      }
    },
    saveUserFeature(fieldsValues) {
      const geom = this.getLayerByName('userLayerDraw')
        .getSource()
        .getFeatures()[0]
        .getGeometry();

      const feature = {
        type: 'Feature',
        geometry: {
          type: geom.getType(),
          coordinates: geom.getCoordinates()
        },
        properties: fieldsValues,
        id: this.editLayer.id
      };

      this.$store.dispatch('postUserLayerGeometry', feature).then(r => {
        if (r.status === 200) {
          const layerFeatures = r.data.features;
          this.getLayerByName('userLayerDraw')
            .getSource()
            .clear();
          this.$root.$emit(
            'showSnackbar',
            this.$i18n.t(`dashboard.map.featureAddSuccess`)
          );

          if (this.getLayerByName(this.editLayer.name_pl)) {
            this.addFeatureToUserLayer(this.editLayer.name_pl, layerFeatures);
          }
          this.isAddNewFeatureActive = false;
        } else {
          this.$root.$emit(
            'showSnackbar',
            this.$i18n.t(`dashboard.map.featureAddError`)
          );
        }
      });
    },
    selectFeature() {
      if (this.getInteractionByName('userDraw')) {
        map.removeInteraction(this.getInteractionByName('userDraw'));
      }
      if (this.getInteractionByName('selectUserFeature')) {
        this.map.removeInteraction(
          this.getInteractionByName('selectUserFeature')
        );
      }
      if (!this.getLayerByName(this.editingLayer)) {
        const layer = this.userLayers.find(
          uL => uL.name_pl === this.editingLayer
        );
        this.addUserLayer(layer);
        this.getLayerByName(layer.name_pl).setVisible(false);
      }
      const selectUserFeature = new Select({
        layers: [this.getLayerByName(this.editingLayer)],
        style: new Style({
          fill: new Fill({
            color: 'rgba(191, 191, 63, 0.7)'
          }),
          stroke: new Stroke({
            color: 'rgba(191, 191, 63, 1)',
            width: 2
          }),
          image: new Circle({
            radius: 5,
            stroke: new Stroke({
              color: 'rgba(191, 191, 63, 1)'
            }),
            fill: new Fill({
              color: 'rgba(191, 191, 63, 0.7)'
            })
          })
        })
      });
      selectUserFeature.set('name', 'selectUserFeature');
      selectUserFeature.on('select', e => {
        if (e.selected.length > 0) {
          this.editLayer = this.$store.getters.getUserLayersList.find(
            uL => uL.name_pl === this.editingLayer
          );
          this.selectedUserFeature = e.selected[0];
          this.isFeatureActive = true;
        }
      });
      this.map.addInteraction(selectUserFeature);
    },
    toggleUserLayer(lay) {
      const layer = this.userLayers.find(uL => uL.name_pl === lay);
      if (this.getLayerByName(layer.name_pl)) {
        if (this.getLayerByName(layer.name_pl).getVisible()) {
          this.getLayerByName(layer.name_pl).setVisible(false);
        } else {
          this.getLayerByName(layer.name_pl).setVisible(true);
        }
      } else {
        this.addUserLayer(layer);
      }
    }
  }
}